import { useFormContext, useFieldArray } from "react-hook-form";
import { Plus, MapPin, AlertTriangle, Loader2, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { TimeBlockInput } from "./TimeBlockInput";
import { ReportShiftIssueDialog } from "./ReportShiftIssueDialog";
import {
  type WeeklyTimeEntryFormData,
  createOvertimeTimeBlock,
  getTimezoneLabel,
  formatDateInTimezone,
  formatTimeInTimezone,
} from "../utils/time-entry-schema";
import { type ShiftNeedingTimeEntry } from "../hooks/useTimeEntryShifts";
import { JobOfferShiftSource, WageFrequency } from "@api/shared-runtime/enums";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { CustomWageNotice } from "./CustomWageNotice";

interface ShiftTimeEntryRowProps {
  shiftIndex: number;
  shift: ShiftNeedingTimeEntry;
  onWithdraw: () => void;
  isWithdrawing: boolean;
  disabled: boolean;
}

export function ShiftTimeEntryRow({
  shiftIndex,
  shift,
  onWithdraw,
  isWithdrawing,
  disabled,
}: ShiftTimeEntryRowProps) {
  const { control, watch } = useFormContext<WeeklyTimeEntryFormData>();

  const {
    fields: timeBlocks,
    append,
    remove,
  } = useFieldArray({
    control,
    name: `shifts.${shiftIndex}.timeBlocks`,
  });

  const shiftDate = new Date(shift.scheduledStartsAt);
  const timezoneId = shift.timezoneId;
  const timezoneLabel = getTimezoneLabel(timezoneId);
  const isOneTime = shift.wageFrequencyId === WageFrequency.OneTime;
  const noOvertime = shift.overtimeWageTypes.length === 0;
  const isAdHoc = shift.source === JobOfferShiftSource.ProviderRequested;

  const handleAddTimeBlock = () => {
    // Get the last time block's end time to use as the new block's start
    const currentBlocks = watch(`shifts.${shiftIndex}.timeBlocks`);
    const lastBlock = currentBlocks[currentBlocks.length - 1];
    const previousEndTime = lastBlock?.endsAt
      ? new Date(lastBlock.endsAt)
      : shiftDate;

    append({
      ...createOvertimeTimeBlock(
        shift.suggestedWageType,
        shiftDate,
        previousEndTime,
      ),
      isOvertime: shift.overtimeWageTypes.includes(shift.suggestedWageType),
    });
  };

  // Format dates and times in the shift's timezone
  const formattedDate = formatDateInTimezone(
    shift.scheduledStartsAt,
    timezoneId,
  );
  const formattedStartTime = formatTimeInTimezone(
    shift.scheduledStartsAt,
    timezoneId,
  );
  const formattedEndTime = formatTimeInTimezone(
    shift.scheduledEndsAt,
    timezoneId,
  );

  return (
    <Card
      className={cn(
        "overflow-hidden",
        shift.hasRejectedEntry && "border-red-300 bg-red-50/30",
      )}
    >
      <CardHeader className="pb-3 bg-gradient-to-r from-slate-50 to-slate-100 border-b p-3">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            {/* Date as primary */}
            <div className="flex items-center gap-2 mb-1">
              <span className="text-lg font-bold text-slate-900">
                {formattedDate}
              </span>
              {shift.hasRejectedEntry && (
                <Badge variant="destructive" className="text-xs">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  Rejected
                </Badge>
              )}
            </div>

            {/* Facility info */}
            <div className="flex items-start gap-1.5 text-sm text-slate-600">
              <MapPin className="h-3.5 w-3.5 text-slate-400 mt-0.5 shrink-0" />
              <div className="flex flex-col sm:flex-row sm:items-center sm:gap-1.5">
                <span className="truncate">{shift.facilityName}</span>
                {shift.facilityCity && shift.facilityState && (
                  <span className="text-slate-400">
                    <span className="hidden sm:inline">• </span>
                    {shift.facilityCity}, {shift.facilityState}
                  </span>
                )}
              </div>
            </div>

            {/* Job offer title if present */}
            {shift.jobOfferTitle && (
              <div className="text-sm text-slate-500 mt-0.5 truncate">
                {shift.jobOfferTitle}
              </div>
            )}

            {shift.customWage && (
              <div className="mt-1">
                <CustomWageNotice customWage={shift.customWage} />
              </div>
            )}
          </div>

          {/* Scheduled time badge */}
          <div className="text-right shrink-0">
            <div className="text-xs text-slate-500 mb-0.5">Scheduled</div>
            <div className="text-sm font-medium text-slate-700">
              {formattedStartTime} - {formattedEndTime}
            </div>
            <div className="text-xs text-slate-400 mt-0.5">
              {timezoneLabel.short}
            </div>
          </div>
        </div>

        {/* Rejection note if any */}
        {shift.hasRejectedEntry && shift.latestRejectionNote && (
          <div className="mt-3 p-3 bg-red-100 border border-red-200 rounded-md">
            <div className="text-sm font-medium text-red-700 mb-1">
              Reason for rejection:
            </div>
            <div className="text-sm text-red-600">
              {shift.latestRejectionNote}
            </div>
          </div>
        )}
      </CardHeader>

      <CardContent className="pt-4 space-y-3 p-3">
        {/* Time block inputs */}
        {timeBlocks.map((block, blockIndex) => (
          <TimeBlockInput
            key={block.id}
            shiftIndex={shiftIndex}
            blockIndex={blockIndex}
            isFirst={blockIndex === 0}
            onRemove={() => remove(blockIndex)}
            shiftDate={shiftDate}
            timezoneId={timezoneId}
            overtimeWageTypes={shift.overtimeWageTypes}
            allowedBaseWageTypes={shift.allowedBaseWageTypes}
          />
        ))}

        {/* Add overtime button */}
        {!isOneTime && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddTimeBlock}
            className="w-full border-dashed text-slate-600 hover:text-blue-600 hover:border-blue-300"
          >
            <Plus className="h-4 w-4 mr-2" />
            {noOvertime
              ? "Add Another Time Block"
              : "Add Another Time Block (Overtime)"}
          </Button>
        )}

        <div className="flex justify-center border-t border-slate-100 pt-2">
          {isAdHoc && shift.hasRejectedEntry && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={disabled}
                >
                  {isWithdrawing ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Undo2 className="mr-2 h-4 w-4" />
                  )}
                  Withdraw hours
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Withdraw rejected hours?</AlertDialogTitle>
                  <AlertDialogDescription>
                    These rejected hours will be removed. You can use Add Hours
                    to submit them again with a different wage set.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={onWithdraw} disabled={disabled}>
                    Withdraw hours
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          {!isAdHoc && <ReportShiftIssueDialog shiftId={shift.shiftId} />}
        </div>
      </CardContent>
    </Card>
  );
}
